import { Component, Input, Output, EventEmitter, OnInit, OnChanges, SimpleChanges, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router } from '@angular/router';
import { MatTableModule, MatTableDataSource } from '@angular/material/table';
import { MatSort, MatSortModule } from '@angular/material/sort';
import { MatIconModule } from '@angular/material/icon';
import { MatSelectModule } from '@angular/material/select';
import { ProgressiveTypeaheadDirective } from '../../../directives/progressive-typeahead.directive';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatChipsModule } from '@angular/material/chips';
import { MatButtonModule } from '@angular/material/button';
import { MatTooltipModule } from '@angular/material/tooltip';
import { FormsModule } from '@angular/forms';
import { PyramidalShareholderPyramidDto } from '../../../models/api/pyramidalShareholderPyramidDto';
import { LargeShareholderCategory } from '../../../models/largeShareholderCategory';
import { LutService } from '../../../services/lut.service';
import { PyramidEntitySummary } from '../../../models/pyramidEntitySummary';
import { PyramidalItemCardComponent, DeleteEntityYearEvent } from '../../large-shareholders/pyramidal-item-card/pyramidal-item-card.component';
import { PyramidsYearCardComponent, DeletePyramidYearEvent } from '../../large-shareholders/pyramids-year-card/pyramids-year-card.component';
import { Helpers } from '../../../utilities/helpers';

interface PyramidDisplayItem {
  id: string;
  directorId?: string;
  pyramidalShareholderId?: string;
  largeShareholderId?: string;
  companyId?: string;
  opbCompanyId?: string;
  name: string;
  type: string;
  categoryName?: string;
  subCategoryName?: string;
  percentage: number;
  percentageYear?: number;
}

@Component({
  selector: 'app-pyramidal-shareholder-pyramid',
  standalone: true,
  imports: [
    CommonModule,
    MatTableModule,
    MatSortModule,
    MatIconModule,
    MatSelectModule,
    ProgressiveTypeaheadDirective,
    MatFormFieldModule,
    MatChipsModule,
    MatButtonModule,
    MatTooltipModule,
    FormsModule,
    PyramidalItemCardComponent,
    PyramidsYearCardComponent
  ],
  templateUrl: './pyramidal-shareholder-pyramid.component.html',
  styleUrls: ['./pyramidal-shareholder-pyramid.component.scss']
})
export class PyramidalShareholderPyramidComponent implements OnInit, OnChanges {
  @Input() pyramidsByYear: { [year: number]: PyramidalShareholderPyramidDto[] } = {};
  @Output() editPyramidItem = new EventEmitter<string>();
  @Output() deletePyramidItem = new EventEmitter<{ pyramidId: string, year?: number }>();

  private lutService = inject(LutService);
  private router = inject(Router);
  protected readonly Helpers = Helpers;

  dataSource = new MatTableDataSource<PyramidDisplayItem>([]);
  displayedColumns: string[] = ['name', 'type', 'categoryName', 'subCategoryName', 'percentage', 'actions'];

  categories: LargeShareholderCategory[] = [];
  availableYears: number[] = [];
  selectedYears: number[] = [];
  pyramidEntitySummaries: PyramidEntitySummary[] = [];
  showEntityTextView = false;
  showYearTextView = false;

  ngOnInit() {
    // Load categories
    this.lutService.getLargeShareholderCategories().subscribe(data => {
      if (data) {
        this.categories = data;
      }
    });

    // Load available years from measurements
    this.availableYears = Helpers.measurements().map(y => parseInt(y, 10)).sort((a, b) => b - a);

    // Get available years from pyramidsByYear
    const pyramidYears = Object.keys(this.pyramidsByYear).map(y => parseInt(y, 10));
    if (pyramidYears.length > 0) {
      // Set the most recent year as selected by default
      this.selectedYears = [Math.max(...pyramidYears)];
    }

    // Process initial data
    this.processData();
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['pyramidsByYear']) {
      // Get available years from pyramidsByYear
      const pyramidYears = Object.keys(this.pyramidsByYear).map(y => parseInt(y, 10));
      if (pyramidYears.length > 0 && this.selectedYears.length === 0) {
        // Set the most recent year as selected by default
        this.selectedYears = [Math.max(...pyramidYears)];
      }
      this.processData();
    }
  }

  onYearsSelectionChange() {
    // Ensure selected years are sorted in descending order
    this.selectedYears.sort((a, b) => b - a);
  }

  getYearName(year: number): string {
    return year.toString();
  }

  selectAllYears() {
    const pyramidYears = Object.keys(this.pyramidsByYear).map(y => parseInt(y, 10));
    this.selectedYears = pyramidYears.length > 0 ? pyramidYears.sort((a, b) => b - a) : [];
  }

  deselectAllYears() {
    this.selectedYears = [];
  }

  getSelectedYearsArray(): number[] {
    return this.selectedYears || [];
  }

  compareYears(year1: number, year2: number): boolean {
    return year1 === year2;
  }

  toggleEntityView() {
    this.showEntityTextView = !this.showEntityTextView;
  }

  toggleYearView() {
    this.showYearTextView = !this.showYearTextView;
  }

  getYearPyramids(year: number): any[] {
    if (!year || !this.pyramidsByYear) {
      return [];
    }
    return this.pyramidsByYear[year] || [];
  }

  private processData() {
    if (!this.pyramidsByYear || Object.keys(this.pyramidsByYear).length === 0) {
      this.pyramidEntitySummaries = [];
      this.dataSource.data = [];
      return;
    }

    // Convert pyramidsByYear to entity summaries using the helper function
    this.pyramidEntitySummaries = Helpers.convertPyramidalShareholderPyramidsByYearToEntitySummaries(this.pyramidsByYear);

    // Process data for the table
    this.processTableData();
  }

  private processTableData() {
    if (!this.pyramidsByYear || Object.keys(this.pyramidsByYear).length === 0) {
      this.dataSource.data = [];
      return;
    }

    // Group by the node's entity id (not by name): the same shareholder can resolve to a
    // blank name in years where its historical basic-info coverage is missing, and grouping
    // by name would split it into a phantom second row. Keyed by entity id, each node appears
    // once, and we surface the most recent year's percentage/name/category.
    const uniqueItems = new Map<string, PyramidDisplayItem>();

    Object.keys(this.pyramidsByYear).forEach(yearKey => {
      const year = parseInt(yearKey, 10);
      const pyramids = this.pyramidsByYear[year];

      pyramids.forEach(pyramid => {
        const entityId = this.getEntityId(pyramid);
        if (!entityId) {
          // A row that references no entity is unusable — skip rather than render a nameless row.
          return;
        }

        const name = this.getPyramidName(pyramid);
        const existing = uniqueItems.get(entityId);

        if (!existing) {
          uniqueItems.set(entityId, {
            id: pyramid.id,
            directorId: pyramid.directorId || undefined,
            pyramidalShareholderId: pyramid.pyramidPyramidalShareholderId || undefined,
            largeShareholderId: pyramid.largeShareholderId || undefined,
            companyId: pyramid.companyId || undefined,
            opbCompanyId: pyramid.opbCompanyId || undefined,
            name: name,
            type: this.getPyramidType(pyramid),
            categoryName: pyramid.categoryName || undefined,
            subCategoryName: pyramid.subCategoryName || undefined,
            percentage: pyramid.percentage,
            percentageYear: year
          });
        } else if (year >= (existing.percentageYear ?? 0)) {
          // A later (or equal) year wins: it carries the most current percentage, row id and
          // — where resolved — name/category.
          existing.id = pyramid.id;
          existing.percentage = pyramid.percentage;
          existing.percentageYear = year;
          if (name) existing.name = name;
          existing.categoryName = pyramid.categoryName || existing.categoryName;
          existing.subCategoryName = pyramid.subCategoryName || existing.subCategoryName;
        } else if (!existing.name && name) {
          // Earlier year but fills in a name the latest year left blank.
          existing.name = name;
        }
      });
    });

    // Sort by latest-year percentage descending (largest holders first), mirroring the
    // Large Shareholder pyramid tab.
    const displayItems = Array.from(uniqueItems.values());
    displayItems.sort((a, b) => (b.percentage || 0) - (a.percentage || 0));

    this.dataSource.data = displayItems;
  }

  private getEntityId(pyramid: PyramidalShareholderPyramidDto): string | undefined {
    return pyramid.pyramidPyramidalShareholderId
      || pyramid.largeShareholderId
      || pyramid.companyId
      || pyramid.opbCompanyId
      || pyramid.directorId
      || undefined;
  }

  private getPyramidName(pyramid: PyramidalShareholderPyramidDto): string {
    return pyramid.pyramidPyramidalShareholderName
      || pyramid.largeShareholderName
      || pyramid.companyName
      || pyramid.opbCompanyName
      || pyramid.directorName
      || '';
  }

  private getPyramidType(pyramid: PyramidalShareholderPyramidDto): string {
    if (pyramid.pyramidPyramidalShareholderId) return 'Pyramidal Shareholder';
    if (pyramid.largeShareholderId) return 'Large Shareholder';
    if (pyramid.companyId) return 'Company';
    if (pyramid.opbCompanyId) return 'OPB Company';
    if (pyramid.directorId) return 'Director';
    return 'Unknown';
  }

  formatPercentage(value: number): string {
    return value ? `${value.toFixed(2)}%` : '';
  }

  getTotalPercentage(): number {
    return this.dataSource.data.reduce((sum, item) => sum + (item.percentage || 0), 0);
  }

  /**
   * Builds the edit-page URL for a pyramid node based on its entity type, so it can be
   * opened in a new tab. Returns '' when the node has no navigable target (e.g. Unknown),
   * in which case the open-in-new-tab icon is hidden.
   */
  getEntityUrl(row: PyramidDisplayItem): string {
    let urlTree;
    switch (row.type) {
      case 'Large Shareholder':
        if (!row.largeShareholderId) return '';
        urlTree = this.router.createUrlTree(['/large-shareholders/edit', row.largeShareholderId]);
        break;
      case 'Pyramidal Shareholder':
        if (!row.pyramidalShareholderId) return '';
        urlTree = this.router.createUrlTree(['/pyramidal-shareholders/edit', row.pyramidalShareholderId]);
        break;
      case 'Company':
        if (!row.companyId) return '';
        urlTree = this.router.createUrlTree(['/companies/edit', row.companyId]);
        break;
      case 'OPB Company':
        if (!row.opbCompanyId) return '';
        urlTree = this.router.createUrlTree(['/companies/opb-edit', row.opbCompanyId]);
        break;
      case 'Director':
        if (!row.directorId) return '';
        urlTree = this.router.createUrlTree(['/directors/edit', row.directorId]);
        break;
      default:
        return '';
    }
    return this.router.serializeUrl(urlTree);
  }

  onEditClick(row: PyramidDisplayItem, event: Event) {
    event.stopPropagation();
    this.editPyramidItem.emit(row.id);
  }

  onDeleteClick(row: PyramidDisplayItem, event: Event) {
    event.stopPropagation();
    this.deletePyramidItem.emit({ pyramidId: row.id });
  }

  onDeleteEntityYear(event: DeleteEntityYearEvent) {
    // Find the pyramid for this entity and year combination
    const year = event.year;
    const pyramids = this.pyramidsByYear[year];

    if (!pyramids) {
      return;
    }

    const pyramid = pyramids.find(p =>
      (p.pyramidPyramidalShareholderId === event.entityId && event.entityType === 'Pyramidal Shareholder') ||
      (p.largeShareholderId === event.entityId && event.entityType === 'Large Shareholder') ||
      (p.companyId === event.entityId && event.entityType === 'Company') ||
      (p.opbCompanyId === event.entityId && event.entityType === 'OPB Company') ||
      (p.directorId === event.entityId && event.entityType === 'Director')
    );

    if (pyramid) {
      // Emit deletion event with year - parent component will handle confirmation
      this.deletePyramidItem.emit({ pyramidId: pyramid.id, year: event.year });
    }
  }

  onDeletePyramidYear(event: DeletePyramidYearEvent) {
    // Emit deletion event with year - parent component will handle confirmation
    this.deletePyramidItem.emit({ pyramidId: event.pyramidId, year: event.year });
  }
}
